/**
 * Reusable Control Definitions
 * Building blocks for chart control panels
 */

import { SINGLE_COLORS, BLUES_PALETTE, REDS_PALETTE, GREENS_PALETTE } from './palettes';
import { EXTERNAL_PRIMARY_COLORS } from '../../config/deploymentProfile';

// ============================================
// COLOR CONTROLS
// ============================================

export const colorControl = {
  key: 'color',
  label: 'Single Color',
  type: 'color',
  options: SINGLE_COLORS.map(c => c.value),
  exclusiveGroup: 'colorMode',
};

export const paletteControl = {
  key: 'paletteGroup',
  label: 'Color Palettes',
  type: 'paletteGroup',
  exclusiveGroup: 'colorMode',
  options: [
    { key: 'useBluesPalette', label: 'Blues', colors: BLUES_PALETTE },
    { key: 'useRedsPalette', label: 'Reds', colors: REDS_PALETTE },
    { key: 'useGreensPalette', label: 'Greens', colors: GREENS_PALETTE },
  ],
};

export const externalPrimaryColorControl = {
  key: 'primaryColor',
  label: 'Primary Color',
  type: 'color',
  options: EXTERNAL_PRIMARY_COLORS.map((option) => option.value),
};

export const externalColorControl = {
  key: 'externalColor',
  label: 'Color',
  type: 'externalColor',
};

export const externalBackgroundControl = {
  key: 'backgroundColor',
  label: 'Background',
  type: 'externalBackground',
};

export const numCategoriesControl = {
  key: 'numCategories',
  label: 'Number of Categories',
  type: 'slider',
  min: 2,
  max: 10,
  step: 1,
  unit: '',
};

export const numSeriesControl = {
  key: 'numSeries',
  label: 'Number of Series',
  type: 'slider',
  min: 2,
  max: 5,
  step: 1,
  unit: '',
};

export const numStacksControl = {
  key: 'numStacks',
  label: 'Number of Stacks',
  type: 'slider',
  min: 2,
  max: 5,
  step: 1,
  unit: '',
};

// ============================================
// AXIS CONTROLS
// ============================================

export const xLabelControl = {
  key: 'xlabel',
  label: 'X-axis Label',
  type: 'text',
  placeholder: 'Enter x-axis label',
};

export const yLabelControl = {
  key: 'ylabel',
  label: 'Y-axis Label',
  type: 'text',
  placeholder: 'Enter y-axis label',
};

export const yMinControl = {
  key: 'yMin',
  label: 'Y-axis Minimum',
  type: 'text',
  placeholder: 'Auto (leave empty for 0)',
};

export const yMaxControl = {
  key: 'yMax',
  label: 'Y-axis Maximum',
  type: 'text',
  placeholder: 'Auto (leave empty)',
};

export const xMinControl = {
  key: 'xMin',
  label: 'X-axis Minimum',
  type: 'text',
  placeholder: 'Auto (leave empty for 0)',
};

export const xMaxControl = {
  key: 'xMax',
  label: 'X-axis Maximum',
  type: 'text',
  placeholder: 'Auto (leave empty)',
};

export const titleControl = {
  key: 'title',
  label: 'Chart Title',
  type: 'text',
  placeholder: 'Enter chart title',
};

// ============================================
// DISPLAY CONTROLS
// ============================================

export const gridControl = {
  key: 'showGrid',
  label: 'Show Grid',
  type: 'toggle',
};

export const labelRotationControl = {
  key: 'labelRotation',
  label: 'X-axis Label Rotation',
  type: 'slider',
  min: 0,
  max: 90,
  step: 15,
  unit: '°',
};

export const truncateXAxisLabelsControl = {
  key: 'truncateXAxisLabels',
  label: 'Truncate X-axis Labels',
  type: 'toggle',
};

export const showValuesControl = {
  key: 'showValues',
  label: 'Show Values on Bars',
  type: 'toggle',
};

export const valueDecimalsControl = {
  key: 'valueDecimals',
  label: 'Value Decimal Places',
  type: 'slider',
  min: 0,
  max: 3,
  step: 1,
  unit: '',
};

// ============================================
// REFERENCE LINE CONTROLS
// ============================================

export const referenceLineControls = [
  {
    key: 'showReferenceLine',
    label: 'Show Reference Line',
    type: 'toggle',
  },
  {
    key: 'referenceValue',
    label: 'Reference Line Value',
    type: 'text',
    placeholder: 'e.g., 85.0',
  },
  {
    key: 'referenceLabel',
    label: 'Reference Line Label',
    type: 'text',
    placeholder: 'e.g., Baseline',
  },
];

// ============================================
// BAR CHART SPECIFIC CONTROLS
// ============================================

export const barOrderingControl = {
  key: 'barOrdering',
  label: 'Bar Ordering',
  type: 'select',
  options: [
    { label: 'Original Order', value: 'original' },
    { label: 'Ascending (Low to High)', value: 'ascending' },
    { label: 'Descending (High to Low)', value: 'descending' },
  ],
};

// ============================================
// LEGEND CONTROLS
// ============================================

export const legendControls = [
  {
    key: 'showLegend',
    label: 'Show Legend',
    type: 'toggle',
  },
  {
    key: 'legendPosition',
    label: 'Legend Position',
    type: 'select',
    options: [
      { label: 'Top', value: 'top' },
      { label: 'Bottom', value: 'bottom' },
      { label: 'Right', value: 'right' },
    ],
  },
];

// ============================================
// CONTROL GROUPS (commonly used together)
// ============================================

export const axisControls = [
  xLabelControl,
  yLabelControl,
  titleControl,
  yMinControl,
  yMaxControl,
];

export const axisControlsHorizontal = [
  xLabelControl,
  yLabelControl,
  titleControl,
  xMinControl,
  xMaxControl,
];

export const colorControls = [
  colorControl,
  paletteControl,
];

export const barDisplayControls = [
  barOrderingControl,
  labelRotationControl,
  gridControl,
  showValuesControl,
  valueDecimalsControl,
];

// ============================================
// CONTROL SECTION DEFINITIONS
// Maps control keys to collapsible UI sections.
// ============================================

export const CONTROL_SECTIONS = [
  { id: 'color', label: 'Colors',        defaultExpanded: true },
  { id: 'text',  label: 'Title & Labels', defaultExpanded: true },
  { id: 'data',  label: 'Data',           defaultExpanded: false },
  { id: 'grid',  label: 'Axes & Grid',    defaultExpanded: false },
  { id: 'style', label: 'Style',          defaultExpanded: false },
];

export const CONTROL_SECTION_MAP = {
  color: 'color',
  paletteGroup: 'color',
  primaryColor: 'color',
  externalColor: 'color',
  backgroundColor: 'color',
  externalBackground: 'color',
  externalColorMode: 'color',
  positiveColor: 'color',
  negativeColor: 'color',
  barColor: 'color',
  lineColor: 'color',
  highlightColor: 'color',
  highlightTop: 'color',
  colorPalette: 'color',

  title: 'text',
  xlabel: 'text',
  ylabel: 'text',
  leftLabel: 'text',
  rightLabel: 'text',

  barOrdering: 'data',
  orientation: 'data',
  showValues: 'data',
  scatterLabels: 'data',
  valueDecimals: 'data',
  numBins: 'data',
  showReferenceLine: 'data',
  referenceValue: 'data',
  referenceLabel: 'data',

  showGrid: 'grid',
  labelRotation: 'grid',
  truncateXAxisLabels: 'grid',
  yMin: 'grid',
  yMax: 'grid',
  xMin: 'grid',
  xMax: 'grid',

  showMarkers: 'style',
  markerSize: 'style',
  markerAlpha: 'style',
  lineWidth: 'style',
  lineThickness: 'style',
  dotSize: 'style',
  barHeight: 'style',
  boxWidth: 'style',
  fillAlpha: 'style',
  showEdges: 'style',
  showCellBorder: 'style',
  showOutliers: 'style',
  showAxisLabels: 'style',
  centerLineStyle: 'style',
  useSeriesStyles: 'style',
  usePatternFills: 'style',
  showLegend: 'style',
  legendPosition: 'style',
};

export const CONDITIONAL_CONTROLS = {
  valueDecimals: 'showValues',
  referenceValue: 'showReferenceLine',
  referenceLabel: 'showReferenceLine',
  legendPosition: 'showLegend',
  highlightColor: 'highlightTop',
  markerSize: 'showMarkers',
};

export const SECTION_THEME_SLIDERS = {
  text: [
    { key: 'labelSize', label: 'Axis Label Size', min: 12, max: 22, default: 15 },
  ],
  data: [
    { key: 'valueLabelSize', label: 'Value Label Size', min: 10, max: 18, default: 12, requires: 'showValues' },
  ],
  grid: [
    { key: 'tickSize', label: 'Tick Size', min: 10, max: 18, default: 13 },
  ],
  style: [
    { key: 'legendSize', label: 'Legend Text Size', min: 10, max: 18, default: 12, requires: 'showLegend' },
    { key: 'legendMarkerSize', label: 'Legend Marker Size', min: 8, max: 24, default: 14, requires: 'showLegend' },
  ],
};
